import { useState } from "react"
import type { Dashboard } from "@/features/dashboard/api/dashboard.queries"
import {
  useCreateSessionMutation,
  useUpdateSessionMutation,
} from "@/features/sleep/api/sleep.mutations"
import { SleepTypeSelector } from "@/features/sleep/SleepTypeSelector"
import { defaultTypeForHour, type SleepType } from "@/features/sleep/sleep.config"
import { useErrorDialog } from "@/shared/components/ErrorDialog"
import { Button } from "@/shared/components/ui/button"
import { JUST_FELL_ASLEEP_MIN, minutesBetween, sleepElapsedLabel } from "@/shared/utils/time"

export function SleepControls({
  dashboard,
  onDone,
}: {
  dashboard: Dashboard
  onDone?: () => void
}) {
  const { showApiError } = useErrorDialog()
  const create = useCreateSessionMutation()
  const update = useUpdateSessionMutation()
  const [type, setType] = useState<SleepType>(() => defaultTypeForHour(new Date().getHours()))

  const current = dashboard.currentSession

  const onWake = () => {
    if (!current) return
    update.mutate(
      {
        id: current.id,
        values: {
          type: current.type,
          startTime: current.startTime,
          endTime: new Date().toISOString(),
        },
      },
      {
        onSuccess: () => onDone?.(),
        onError: (err) => showApiError(err, { title: "Kunde inte avsluta passet" }),
      },
    )
  }

  const onSleep = () => {
    create.mutate(
      {
        childId: dashboard.child.id,
        values: { type, startTime: new Date().toISOString(), endTime: null },
      },
      {
        onSuccess: () => onDone?.(),
        onError: (err) => showApiError(err, { title: "Kunde inte starta passet" }),
      },
    )
  }

  if (current) {
    const minutesAsleep = minutesBetween(Date.now(), new Date(current.startTime).getTime())
    return (
      <div className="flex flex-col gap-3">
        {minutesAsleep >= JUST_FELL_ASLEEP_MIN && (
          <p className="text-center text-sm text-muted-foreground">
            {sleepElapsedLabel(minutesAsleep)}
          </p>
        )}
        <Button
          size="lg"
          className="h-14 w-full text-base"
          isLoading={update.isPending}
          onClick={onWake}
        >
          Vakna
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <SleepTypeSelector value={type} onChange={setType} />
      <Button
        size="lg"
        className="h-14 w-full text-base"
        isLoading={create.isPending}
        onClick={onSleep}
      >
        Somna
      </Button>
    </div>
  )
}
